import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Database, Link2, Loader2, Mic, Plus, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { DATAFORFRAGAN_TYPER } from "../lib/constants";
import { Modal, inputCls, labelCls } from "./Modals";

export default function LinksModal({ open, onClose, projectId, buoys, refresh }) {
    const [data, setData] = useState({ surveys: [], meetings: [], data_requests: [] });
    const [drTyp, setDrTyp] = useState("sjukstatistik");
    const [drTitle, setDrTitle] = useState("");
    const [drInstr, setDrInstr] = useState("");
    const [drBuoy, setDrBuoy] = useState("");
    const [drHours, setDrHours] = useState(168);
    const [fragor, setFragor] = useState([""]);
    const [surveyBuoy, setSurveyBuoy] = useState("");
    const [surveyHours, setSurveyHours] = useState(168);
    const [moteTitle, setMoteTitle] = useState("");
    const [moteBuoy, setMoteBuoy] = useState("");
    const [moteHours, setMoteHours] = useState(72);
    const [busy, setBusy] = useState(null);
    const [responses, setResponses] = useState({});

    const load = () => {
        if (projectId) api.get(`/projects/${projectId}/links`).then((r) => setData(r.data)).catch(() => {});
    };
    useEffect(() => { if (open) load(); }, [open, projectId]);

    const copy = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Länk kopierad");
        } catch {
            toast.error("Kunde inte kopiera länken");
        }
    };

    const createSurvey = async () => {
        setBusy("survey");
        try {
            await api.post(`/projects/${projectId}/surveys`, {
                fragor: fragor.filter((f) => f.trim()), giltig_timmar: Number(surveyHours), buoy_id: surveyBuoy || null,
            });
            toast.success("Frågelänk skapad");
            setFragor([""]);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const compileSurvey = async (sid) => {
        setBusy(`compile-${sid}`);
        try {
            await api.post(`/surveys/${sid}/compile`);
            toast.success("AI-sammanställning skapad – granska den i AI-assistenten");
            refresh?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const toggleResponses = async (sid) => {
        if (responses[sid]) {
            setResponses({ ...responses, [sid]: null });
            return;
        }
        const r = await api.get(`/surveys/${sid}/responses`);
        setResponses({ ...responses, [sid]: r.data });
    };

    const createMeeting = async () => {
        setBusy("mote");
        try {
            await api.post(`/projects/${projectId}/meeting-links`, {
                title: moteTitle, buoy_id: moteBuoy || null, giltig_timmar: Number(moteHours),
            });
            toast.success("Möteslänk skapad");
            setMoteTitle("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const createDataRequest = async () => {
        setBusy("datareq");
        try {
            await api.post(`/projects/${projectId}/data-requests`, {
                typ: drTyp, title: drTitle, instruktion: drInstr, buoy_id: drBuoy || null, giltig_timmar: Number(drHours),
            });
            toast.success("Dataförfrågan skapad – skicka länken till företaget");
            setDrTitle("");
            setDrInstr("");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };

    const buoyLabel = (bid) => buoys.find((b) => b.id === bid)?.etikett;

    return (
        <Modal open={open} onClose={onClose} title="Medarbetarlänkar" testid="links-modal" wide>
            <p className="text-xs text-slate-500 leading-relaxed mb-5">
                Skicka tillfälliga, anonyma länkar till medarbetare. Svar pseudonymiseras (Medarbetare 1, 2, …) och
                mötesinspelningar transkriberas + av-identifieras – du granskar allt innan det blir empiri.
            </p>

            <section className="mb-6">
                <h4 className="font-display font-bold text-slate-100 mb-3 flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-current" /> Frågelänkar
                </h4>
                <div className="space-y-2 mb-4">
                    {data.surveys.length === 0 && <p className="text-xs text-slate-600">Inga frågelänkar ännu.</p>}
                    {data.surveys.map((s) => (
                        <div key={s.id} data-testid={`survey-row-${s.id}`} className="rounded-lg border border-border bg-abyss/40 p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.aktiv_nu ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                                    {s.aktiv_nu ? "Aktiv" : "Stängd"}
                                </span>
                                <span className="font-mono text-[10px] text-slate-500">
                                    {s.antal_svar} svar · utgår {new Date(s.expires_at).toLocaleString("sv-SE")}{s.buoy_id ? ` · ${buoyLabel(s.buoy_id) || "boj"}` : ""}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button data-testid={`survey-copy-${s.id}`} onClick={() => copy(`${window.location.origin}/undersokning/${s.token}`)}
                                        className="text-current hover:text-tide transition-colors" title="Kopiera länk"><Copy className="w-3.5 h-3.5" /></button>
                                    {s.aktiv_nu && (
                                        <button data-testid={`survey-close-${s.id}`} onClick={async () => { await api.delete(`/surveys/${s.id}`); load(); }}
                                            className="text-slate-500 hover:text-red-400 text-xs transition-colors">Stäng</button>
                                    )}
                                    <button data-testid={`survey-compile-${s.id}`} onClick={() => compileSurvey(s.id)}
                                        disabled={busy === `compile-${s.id}` || s.antal_svar === 0}
                                        className="text-xs text-aidraft border border-aidraft/40 rounded-lg px-2 py-1 hover:bg-aidraft/10 transition-colors disabled:opacity-40 flex items-center gap-1">
                                        {busy === `compile-${s.id}` && <Loader2 className="w-3 h-3 animate-spin" />}
                                        Sammanställ med AI
                                    </button>
                                </div>
                            </div>
                            <p className="text-xs text-slate-400 mt-1.5 truncate" title={(s.fragor || []).join("\n")}>
                                {s.fragor?.[0]}{s.fragor?.length > 1 ? ` (+${s.fragor.length - 1} fråga${s.fragor.length > 2 ? "r" : ""} till)` : ""}
                            </p>
                            {s.antal_svar > 0 && (
                                <button data-testid={`survey-responses-toggle-${s.id}`} onClick={() => toggleResponses(s.id)}
                                    className="text-[11px] text-slate-500 hover:text-slate-300 mt-2 transition-colors">
                                    {responses[s.id] ? "Dölj svar" : `Visa svar (${s.antal_svar})`}
                                </button>
                            )}
                            {responses[s.id] && (
                                <div className="space-y-2 mt-2">
                                    {responses[s.id].map((r) => (
                                        <div key={r.id} className="rounded-lg bg-abyss/60 border border-border/60 p-2.5">
                                            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">{r.pseudonym}</p>
                                            {r.svar.map((sv, i) => (
                                                <div key={i} className="mt-1">
                                                    <p className="text-[11px] font-semibold text-slate-300">{sv.fraga}</p>
                                                    <p className="text-[11px] text-slate-400">{sv.svar}</p>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
                <div className="border-t border-border pt-3 space-y-2">
                    <p className={labelCls}>Ny frågelänk</p>
                    {fragor.map((f, i) => (
                        <div key={i} className="flex gap-2">
                            <input data-testid={`survey-question-${i}`} className={inputCls} value={f}
                                placeholder={`Fråga ${i + 1}, t.ex. "Vad tar längst tid i din vardag?"`}
                                onChange={(e) => setFragor(fragor.map((x, j) => (j === i ? e.target.value : x)))} />
                            {fragor.length > 1 && (
                                <button data-testid={`survey-question-remove-${i}`} onClick={() => setFragor(fragor.filter((_, j) => j !== i))}
                                    className="text-slate-600 hover:text-red-400"><X className="w-4 h-4" /></button>
                            )}
                        </div>
                    ))}
                    <div className="flex gap-2 flex-wrap">
                        <button data-testid="survey-add-question" onClick={() => setFragor([...fragor, ""])}
                            className="text-xs text-current hover:text-tide transition-colors flex items-center gap-1">
                            <Plus className="w-3.5 h-3.5" /> Lägg till fråga
                        </button>
                        <div className="flex-1" />
                        <select data-testid="survey-buoy-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1 text-xs text-slate-300 outline-none max-w-[160px]"
                            value={surveyBuoy} onChange={(e) => setSurveyBuoy(e.target.value)}>
                            <option value="">Koppla boj (valfritt)</option>
                            {buoys.map((b) => <option key={b.id} value={b.id}>{b.etikett}</option>)}
                        </select>
                        <select data-testid="survey-validity-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1 text-xs text-slate-300 outline-none"
                            value={surveyHours} onChange={(e) => setSurveyHours(e.target.value)}>
                            <option value={72}>3 dygn</option>
                            <option value={168}>7 dygn</option>
                            <option value={336}>14 dygn</option>
                        </select>
                    </div>
                    <button data-testid="create-survey-button" onClick={createSurvey} disabled={busy === "survey" || !fragor.some((f) => f.trim())}
                        className="w-full flex items-center justify-center gap-2 bg-current/15 border border-current/40 text-current rounded-lg py-2 text-sm font-semibold hover:bg-current/25 transition-colors disabled:opacity-40">
                        {busy === "survey" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        Skapa frågelänk
                    </button>
                </div>
            </section>

            <section>
                <h4 className="font-display font-bold text-slate-100 mb-1 flex items-center gap-2">
                    <Mic className="w-4 h-4 text-buoy" /> Möteslänkar (inspelning → transkribering)
                </h4>
                <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
                    Mottagaren öppnar länken, spelar in mötet i webbläsaren och skickar. Ljudet transkriberas (Whisper) och
                    av-identifieras direkt – råljudet lagras aldrig. Transkriberingen landar i bojens granskningsruta.
                </p>
                <div className="space-y-2 mb-4">
                    {data.meetings.length === 0 && <p className="text-xs text-slate-600">Inga möteslänkar ännu.</p>}
                    {data.meetings.map((m) => (
                        <div key={m.id} data-testid={`meeting-row-${m.id}`} className="rounded-lg border border-border bg-abyss/40 p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-200">{m.title}</span>
                                <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.aktiv_nu ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                                    {m.aktiv_nu ? "Aktiv" : "Stängd"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-1.5">
                                <span className="font-mono text-[10px] text-slate-500">
                                    utgår {new Date(m.expires_at).toLocaleString("sv-SE")}{m.buoy_id ? ` · ${buoyLabel(m.buoy_id) || "ny boj skapas"}` : " · ny boj skapas"}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button data-testid={`meeting-copy-${m.id}`} onClick={() => copy(`${window.location.origin}/mote/${m.token}`)}
                                        className="text-current hover:text-tide transition-colors" title="Kopiera länk"><Copy className="w-3.5 h-3.5" /></button>
                                    {m.aktiv_nu && (
                                        <button data-testid={`meeting-close-${m.id}`} onClick={async () => { await api.delete(`/meeting-links/${m.id}`); load(); }}
                                            className="text-slate-500 hover:text-red-400 text-xs transition-colors">Stäng</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="border-t border-border pt-3 space-y-2">
                    <p className={labelCls}>Ny möteslänk</p>
                    <input data-testid="meeting-title-input" className={inputCls} value={moteTitle}
                        placeholder="T.ex. Avdelningsmöte ekonomi, v.37" onChange={(e) => setMoteTitle(e.target.value)} />
                    <div className="flex gap-2 flex-wrap">
                        <select data-testid="meeting-buoy-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1.5 text-xs text-slate-300 outline-none flex-1 min-w-[140px]"
                            value={moteBuoy} onChange={(e) => setMoteBuoy(e.target.value)}>
                            <option value="">Ny boj skapas automatiskt</option>
                            {buoys.map((b) => <option key={b.id} value={b.id}>{b.etikett}</option>)}
                        </select>
                        <select data-testid="meeting-validity-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1.5 text-xs text-slate-300 outline-none"
                            value={moteHours} onChange={(e) => setMoteHours(e.target.value)}>
                            <option value={24}>24 timmar</option>
                            <option value={72}>3 dygn</option>
                            <option value={168}>7 dygn</option>
                        </select>
                    </div>
                    <button data-testid="create-meeting-button" onClick={createMeeting} disabled={busy === "mote" || !moteTitle.trim()}
                        className="w-full flex items-center justify-center gap-2 bg-buoy/15 border border-buoy/40 text-buoy rounded-lg py-2 text-sm font-semibold hover:bg-buoy/25 transition-colors disabled:opacity-40">
                        {busy === "mote" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                        Skapa möteslänk
                    </button>
                </div>
            </section>

            <section className="border-t border-border pt-4">
                <h4 className="font-display font-bold text-slate-100 mb-1 flex items-center gap-2">
                    <Database className="w-4 h-4 text-amber-400" /> Dataförfrågningar (insamlingsagenter)
                </h4>
                <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
                    Be företaget om en export – de öppnar länken, godkänner utlämningen och laddar upp anonymiserad data.
                    AI aggregerar och av-identifierar (hälsodata endast på aggregerad nivå) – resultatet landar i bojens granskningsruta.
                </p>
                <div className="space-y-2 mb-4">
                    {(data.data_requests || []).length === 0 && <p className="text-xs text-slate-600">Inga dataförfrågningar ännu.</p>}
                    {(data.data_requests || []).map((d) => (
                        <div key={d.id} data-testid={`datareq-row-${d.id}`} className="rounded-lg border border-border bg-abyss/40 p-3">
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 shrink-0">
                                        {DATAFORFRAGAN_TYPER[d.typ]?.label || d.typ}
                                    </span>
                                    <span className="text-sm font-semibold text-slate-200 truncate">{d.title}</span>
                                </div>
                                <span className={`font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${d.aktiv_nu ? "text-buoy border-buoy/40 bg-buoy/10" : "text-slate-500 border-slate-600"}`}>
                                    {d.aktiv_nu ? "Aktiv" : "Stängd"}
                                </span>
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-1.5">
                                <span className="font-mono text-[10px] text-slate-500">
                                    {d.antal_material} material · utgår {new Date(d.expires_at).toLocaleString("sv-SE")}{d.buoy_id ? ` · ${buoyLabel(d.buoy_id) || "boj"}` : " · ny boj skapas"}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button data-testid={`datareq-copy-${d.id}`} onClick={() => copy(`${window.location.origin}/insamling/${d.token}`)}
                                        className="text-current hover:text-tide transition-colors" title="Kopiera länk"><Copy className="w-3.5 h-3.5" /></button>
                                    {d.aktiv_nu && (
                                        <button data-testid={`datareq-close-${d.id}`} onClick={async () => { await api.delete(`/data-requests/${d.id}`); load(); }}
                                            className="text-slate-500 hover:text-red-400 text-xs transition-colors">Stäng</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="space-y-2">
                    <p className={labelCls}>Ny dataförfrågan</p>
                    <select data-testid="datareq-type-select" className={inputCls} value={drTyp} onChange={(e) => setDrTyp(e.target.value)}>
                        {Object.entries(DATAFORFRAGAN_TYPER).map(([id, t]) => <option key={id} value={id}>{t.label}</option>)}
                    </select>
                    <p className="text-[10px] text-slate-500 leading-relaxed">{DATAFORFRAGAN_TYPER[drTyp]?.hint}</p>
                    <input data-testid="datareq-title-input" className={inputCls} value={drTitle}
                        placeholder="T.ex. Sjukstatistik Q2 2026" onChange={(e) => setDrTitle(e.target.value)} />
                    <textarea data-testid="datareq-instr-input" className={inputCls} rows={2} value={drInstr}
                        placeholder="Instruktion till mottagaren, t.ex. 'Exportera från HR-systemet som CSV, aggregerat per avdelning och månad'"
                        onChange={(e) => setDrInstr(e.target.value)} />
                    <div className="flex gap-2 flex-wrap">
                        <select data-testid="datareq-buoy-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1.5 text-xs text-slate-300 outline-none flex-1 min-w-[140px]"
                            value={drBuoy} onChange={(e) => setDrBuoy(e.target.value)}>
                            <option value="">Ny boj skapas automatiskt</option>
                            {buoys.map((b) => <option key={b.id} value={b.id}>{b.etikett}</option>)}
                        </select>
                        <select data-testid="datareq-validity-select" className="bg-abyss/60 border border-border rounded-lg px-2 py-1.5 text-xs text-slate-300 outline-none"
                            value={drHours} onChange={(e) => setDrHours(e.target.value)}>
                            <option value={72}>3 dygn</option>
                            <option value={168}>7 dygn</option>
                            <option value={336}>14 dygn</option>
                        </select>
                    </div>
                    <button data-testid="create-datareq-button" onClick={createDataRequest} disabled={busy === "datareq" || !drTitle.trim()}
                        className="w-full flex items-center justify-center gap-2 bg-amber-500/15 border border-amber-500/40 text-amber-400 rounded-lg py-2 text-sm font-semibold hover:bg-amber-500/25 transition-colors disabled:opacity-40">
                        {busy === "datareq" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
                        Skapa dataförfrågan
                    </button>
                </div>
            </section>
        </Modal>
    );
}
